/* eslint-disable no-console */
// stores/messageStore.ts
import { action, observable } from 'mobx';
import { Message, SubscriptionsInterface } from '../api/socket.types';
import { socketController } from './socketController';
import { getUniqueDeviceIdForTab } from 'lib/api';
import { socketService } from './socketService';
import { invoicesActions } from 'modules/orders/store/invoices/invoicesStore';
import { evacuationActions } from 'modules/orders/store/evacuation/evacuationStore';

export interface MessageStore {
  messages: Message[];
  // currentSubscription: SubscriptionItemInterface,
  isLoading: boolean;
  error: string | null;
}

// Map для хранения таймеров удаления по ID сообщения
const removalTimeouts = new Map<string, NodeJS.Timeout>();

const initialValues: MessageStore = {
  messages: [],
  // currentSubscription: null,
  isLoading: false,
  error: null,
};

export const messageStore = observable(initialValues);

export const messageActions = {
  addMessage: action((message: Message) => {
    // Добавляем поле read: false по умолчанию для новых сообщений
    const messageWithRead = { ...message, read: message.read || false };
    messageStore.messages.push(messageWithRead);

    // 🔥 Обработка события оплаты сразу при получении
    if (
      messageWithRead.type === 'event' &&
    messageWithRead.content &&
    typeof messageWithRead.content === 'object' &&
    'eventType' in messageWithRead.content &&
    messageWithRead.content.eventType === 'ORDER_INVOICE_STATUS_CHANGED'
    ) {
      try {
        invoicesActions.setPaidInvoice(messageWithRead.content);
      } catch {
        // console.error('Failed to process paid invoice from socket message:', e);
      }
    }

    // 🔥 Обработка события создания нового заказа (ЭВАКУАТОР)
    if (
      messageWithRead.type === 'event' &&
      messageWithRead.content &&
      typeof messageWithRead.content === 'object' &&
      'eventType' in messageWithRead.content &&
      messageWithRead.content.eventType === 'ORDER_CREATED'
    ) {
      try {
        console.log('[Socket] ORDER_CREATED event received:', messageWithRead.content);

        // Сохраняем ID новой заявки для подсветки жирным шрифтом
        const newOrderId = messageWithRead.content?.orderId;
        if (newOrderId) {
          evacuationActions.addNewOrderId(newOrderId);
        }

        // Вызываем дебаунс-обновление списка эвакуатор-заявок
        evacuationActions.refreshEvacuatorOrdersList();

        // TODO: [Этап 2] Добавить визуальное уведомление, если нужно:
        // toastr.info('🚚', 'Поступила новая заявка на эвакуацию');

      } catch (e) {
        console.error('[Socket] Failed to process ORDER_CREATED event:', e);
        // Не пробрасываем ошибку, чтобы не ломать обработку других сообщений
      }
    }
    
    // Запускаем таймер на удаление сообщения через 10 секунд
    messageActions.scheduleMessageRemoval(messageWithRead.id);
  }),

  scheduleMessageRemoval: action((messageId: string) => {
    // Очищаем существующий таймер, если он есть
    const existingTimeout = removalTimeouts.get(messageId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Устанавливаем новый таймер
    const timeout = setTimeout(() => {
      messageActions.removeMessage(messageId);
    }, 10000); // 10 секунд = 10000 миллисекунд
    
    removalTimeouts.set(messageId, timeout);
  }),

  removeMessage: action((messageId: string) => {
    // Удаляем сообщение из массива
    messageStore.messages = messageStore.messages.filter(msg => msg.id !== messageId);
    
    // Очищаем таймер из Map
    const timeout = removalTimeouts.get(messageId);
    if (timeout) {
      clearTimeout(timeout);
      removalTimeouts.delete(messageId);
    }
  }),

  cancelScheduledRemoval: (messageId: string) => {
    // Отменяем запланированное удаление сообщения
    const timeout = removalTimeouts.get(messageId);
    if (timeout) {
      clearTimeout(timeout);
      removalTimeouts.delete(messageId);
    }
  },

  markAsRead: action((messageId: string) => {
    const message = messageStore.messages.find(msg => msg.id === messageId);
    if (message) {
      message.read = true;
      // Можно добавить логику для отмены удаления прочитанных сообщений, если нужно
      // messageActions.cancelScheduledRemoval(messageId);
    }
  }),

  markAllAsRead: action(() => {
    messageStore.messages.forEach(message => {
      message.read = true;
      // Если нужно отменить удаление всех прочитанных сообщений:
      // messageActions.cancelScheduledRemoval(message.id);
    });
  }),

  clearMessages: action(() => {
    // Очищаем все таймеры перед очисткой сообщений
    removalTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    removalTimeouts.clear();
    
    // Очищаем сообщения
    messageStore.messages = [];
  }),

  setLoading: action((loading: boolean) => {
    messageStore.isLoading = loading;
  }),

  setError: action((error: string | null) => {
    messageStore.error = error;
  }),

  get unreadCount(): number {
    return messageStore.messages.filter(msg => !msg.read).length;
  },

  get unreadNotifications(): Message[] {
    return messageStore.messages.filter(msg => 
      (msg.type === 'notification' || msg.type === 'push') && !msg.read
    );
  },

  get lastMessage(): Message | null {
    return messageStore.messages.length > 0 
      ? messageStore.messages[messageStore.messages.length - 1] 
      : null;
  },

  get messageCount(): number {
    return messageStore.messages.length;
  },

  // stores/messageStore.ts

  createOrderPaymentSubscription: async (orderId: string): Promise<string | null> => {
  // 1. Гарантируем подключение к сокету
    if (!socketService.isConnected()) {
      console.log('[Socket] Connecting for order payment subscription...');
      socketService.connect(); // это асинхронно, но connect() запускает процесс

      // Ждём, пока сокет подключится (с таймаутом на случай ошибки)
      const connected = await new Promise<boolean>((resolve) => {
        const maxWait = 5000; // 5 секунд
        const startTime = Date.now();

        const checkConnection = () => {
          if (socketService.isConnected()) {
            resolve(true);
          } else if (Date.now() - startTime > maxWait) {
            resolve(false);
          } else {
            setTimeout(checkConnection, 100);
          }
        };

        checkConnection();
      });

      if (!connected) {
        throw new Error('Failed to connect to WebSocket server');
      }
    }

    // 2. Получаем deviceId
    const uniqueDeviceId = getUniqueDeviceIdForTab();
    if (!uniqueDeviceId) {
      throw new Error('Device ID is missing');
    }

    // 3. Формируем запрос
    const request: SubscriptionsInterface = { 
      scope: 'ORDER',
      deviceId: uniqueDeviceId,
      eventTypes: ['ORDER_INVOICE_STATUS_CHANGED'],
      orderId,
    };

    // 4. Создаём подписку
    try {
      const response = await socketController.createOrderPaymentSubscription(request);
      const subscriptionId = response?.data?.id ?? null;
      console.log('[Socket] Subscription created for order:', orderId, 'ID:', subscriptionId);
      return subscriptionId;
    } catch (error) {
      console.error('[Socket] Failed to create subscription:', error);
      messageActions.setError('Ошибка при создании подписки на оплату');
      throw error;
    }
  },
  deleteOrderPaymentSubscription: async (subscriptionId: string): Promise<void> => {
    if (!subscriptionId) {
      console.warn('[Socket] Attempt to delete empty subscription ID');
      return;
    }

    try {
      await socketController.deleteOrderPaymentSubscription(subscriptionId);
      console.log('[Socket] Subscription deleted:', subscriptionId);
    } catch (error) {
      console.error('[Socket] Failed to delete subscription:', error);
    // Не бросаем — чтобы не мешать закрытию
    }
  },

  createNewOrderSubscription: async (): Promise<string | null> => {
  // 1. Гарантируем подключение к сокету
    if (!socketService.isConnected()) {
      console.log('[Socket] Connecting for new order subscription...');
      socketService.connect();
      const connected = await new Promise<boolean>((resolve) => {
        const maxWait = 5000;
        const startTime = Date.now();
        const check = () => {
          if (socketService.isConnected()) resolve(true);
          else if (Date.now() - startTime > maxWait) resolve(false);
          else setTimeout(check, 100);
        };
        check();
      });
      if (!connected) throw new Error('Failed to connect to WebSocket server');
    }

    // 2. Получаем deviceId
    const uniqueDeviceId = getUniqueDeviceIdForTab();
    if (!uniqueDeviceId) throw new Error('Device ID is missing');

    // 3. Формируем запрос (массив eventTypes, scope GLOBAL, orderId не нужен)
    const request: SubscriptionsInterface = {
      scope: 'GLOBAL',
      deviceId: uniqueDeviceId,
      eventTypes: ['ORDER_CREATED'],
    };

    // 4. Создаём подписку (переиспользуем существующий метод контроллера)
    try {
      const response = await socketController.createOrderPaymentSubscription(request);
      const subscriptionId = response?.data?.id ?? null;
      console.log('[Socket] New order subscription created, ID:', subscriptionId);
      return subscriptionId;
    } catch (error) {
      console.error('[Socket] Failed to create new order subscription:', error);
      messageActions.setError('Ошибка при создании подписки на новые заказы');
      throw error;
    }
  },

  deleteNewOrderSubscription: async (subscriptionId: string): Promise<void> => {
    if (!subscriptionId) {
      console.warn('[Socket] Attempt to delete empty subscription ID');
      return;
    }
    try {
    // Переиспользуем метод удаления (один REST-эндпоинт)
      await socketController.deleteOrderPaymentSubscription(subscriptionId);
      console.log('[Socket] New order subscription deleted:', subscriptionId);
    } catch (error) {
      console.error('[Socket] Failed to delete new order subscription:', error);
    // Не бросаем ошибку, чтобы не ломать unmount-жизненный цикл
    }
  },

  // ... существующий код ...

  // Опционально: функция для очистки всех ресурсов при уничтожении стора
  cleanup: () => {
    removalTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    removalTimeouts.clear();
  },
};